import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Machine } from '../../types';
import { apiClient } from '../../services/apiClient';
import './SelectionModal.css';

interface MachineSelectionModalProps {
  onSelect: (machine: Machine) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const MachineSelectionModal: React.FC<MachineSelectionModalProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const searchMachines = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setMachines([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.searchMachines(query);
      setMachines(data);
      setHasSearched(true);
    } catch (err) {
      setError(t('messages.errorLoadingData'));
      setMachines([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      searchMachines(searchQuery);
    }, DEBOUNCE_MS);

    // Cleanup on unmount or query change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, searchMachines]);

  const handleSelect = (machine: Machine) => {
    onSelect(machine);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('machineForm.selectMachine')}</h3>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          <input
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="filter-input"
            autoFocus
          />

          <div className="search-results-container">
            {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_LENGTH && (
              <div className="search-hint">
                {t('machineForm.minSearchChars', { count: MIN_SEARCH_LENGTH })}
              </div>
            )}

            {loading && <div className="loading">{t('app.loading')}</div>}

            {error && <div className="error-message">{error}</div>}

            {!loading && !error && searchQuery.length === 0 && (
              <div className="search-hint">
                {t('machineForm.typeToSearch')}
              </div>
            )}

            {!loading && !error && hasSearched && (
              <div className="selection-list">
                {machines.map((machine) => (
                  <div
                    key={machine.id}
                    className="selection-item"
                    onClick={() => handleSelect(machine)}
                  >
                    <div className="item-main">{machine.serialNumber || machine.id}</div>
                    {machine.displayDescription && <div className="item-details">{machine.displayDescription}</div>}
                    {machine.status && <div className="item-details">{t('reportTable.status')}: {machine.status}</div>}
                  </div>
                ))}
                {machines.length === 0 && (
                  <div className="no-results">{t('mainForm.noResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineSelectionModal;
